import { es } from 'date-fns/locale';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { DateRange, RangeKeyDict } from 'react-date-range';

import { Button } from '~components/index';
import { tnl } from '~i18n/i18n';
import { formatDate } from '~utils/moment';
import InputLabel from '../InputLabel/InputLabel';
import './inputDateRange.scss';
import '../inputs.scss';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface Props extends CommonInputProps {
  nameStartDate?: string;
  nameEndDate?: string;
  defaultValueStartDate?: string;
  defaultValueEndDate?: string;
  min?: string;
  max?: string;
  hookOnChange?: (value: Record<string, string | undefined>) => void;
  onInputChange?: (value: Record<string, string | undefined>) => void;
}

const InputDateRange = ({
  name,
  label,
  labelHelper,
  nameStartDate,
  nameEndDate,
  defaultValueStartDate,
  defaultValueEndDate,
  disabled = false,
  required = false,
  min,
  max,
  hookError,
  design = 'col-12',
  onInputChange = () => {},
  hookOnChange = () => {}
}: Props) => {
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectingEndDate, setSelectingEndDate] = useState(false);

  const debouncedSetValue = debounce(
    (value: Record<string, string | undefined>) => {
      hookOnChange(value);
      onInputChange(value);
    },
    200
  );

  const initialRange = {
    startDate: defaultValueStartDate
      ? new Date(defaultValueStartDate)
      : undefined,
    endDate: defaultValueEndDate ? new Date(defaultValueEndDate) : undefined,
    key: 'selection'
  };

  const [state, setState] = useState([initialRange]);
  const isRangeEmpty = !state[0].startDate && !state[0].endDate;
  const currentDate = new Date();

  const handleSelect = (ranges: RangeKeyDict) => {
    const { startDate, endDate } = ranges.selection;

    if (!selectingEndDate) {
      setState([{ startDate, endDate: state[0].endDate, key: 'selection' }]);
      setSelectingEndDate(true);
    } else {
      const updatedState = [{ startDate, endDate, key: 'selection' }];
      setState(updatedState);
      setShowCalendar(false);

      debouncedSetValue({
        [nameStartDate!]: startDate ? formatDate(startDate, 'YYYY-MM-DD') : '',
        [nameEndDate!]: endDate ? formatDate(endDate, 'YYYY-MM-DD') : ''
      });
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      calendarRef.current &&
      !calendarRef.current.contains(event.target as Node)
    ) {
      setShowCalendar(false);
      setSelectingEndDate(false);
    }
  };

  const handleReset = () => {
    setState([initialRange]);
    debouncedSetValue({
      [nameStartDate!]: undefined,
      [nameEndDate!]: undefined
    });
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      className={`customInput ${design} ${
        disabled ? 'customInput_disabled' : ''
      }`}
    >
      {label && (
        <InputLabel
          name={name}
          text={label}
          helper={labelHelper}
          required={required}
        />
      )}
      <div className="customInput_container px-2">
        <Button
          variant="link"
          iconName="trashFill"
          title="labels.date_reset"
          onClick={handleReset}
          disabled={isRangeEmpty}
        />
        <p
          className={`fw-bold ${
            !selectingEndDate && showCalendar ? 'highlight' : ''
          }`}
        >
          {tnl('labels.start_date_range')}:
        </p>
        <input
          type="date"
          id="start-date"
          name="start-date"
          value={
            state[0].startDate
              ? formatDate(state[0].startDate, 'YYYY-MM-DD')
              : ''
          }
          readOnly
          onFocus={() => {
            setShowCalendar(true);
            setSelectingEndDate(false);
          }}
          disabled={disabled}
          required={required}
          className="custom-input"
        />
        <p
          className={`fw-bold ${
            selectingEndDate && showCalendar ? 'highlight' : ''
          }`}
        >
          {tnl('labels.end_date_range')}:
        </p>
        <input
          type="date"
          id="end-date"
          name="end-date"
          value={
            state[0].endDate ? formatDate(state[0].endDate, 'YYYY-MM-DD') : ''
          }
          readOnly
          onFocus={() => {
            setShowCalendar(true);
            setSelectingEndDate(true);
          }}
          disabled={disabled}
          required={required}
          className="custom-input"
        />
        {showCalendar && (
          <div className="calendar-wrapper" ref={calendarRef}>
            <DateRange
              ranges={[
                {
                  startDate: isRangeEmpty ? currentDate : state[0].startDate,
                  endDate: isRangeEmpty ? currentDate : state[0].endDate,
                  key: 'selection'
                }
              ]}
              onChange={handleSelect}
              showDateDisplay={false}
              editableDateInputs={true}
              moveRangeOnFirstSelection={true}
              minDate={min ? new Date(min) : undefined}
              maxDate={max ? new Date(max) : undefined}
              focusedRange={selectingEndDate ? [0, 1] : [0, 0]}
              rangeColors={[
                isRangeEmpty
                  ? 'var(--app-primary-color)'
                  : 'var(--app-primary-color)'
              ]}
              locale={es}
            />
          </div>
        )}
      </div>
      {hookError?.message && (
        <p className="customInput_error">{tnl(hookError?.message)}</p>
      )}
    </div>
  );
};

export default InputDateRange;
